import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
// ─────────────────────────────────────────────────────────────────────────────
// 19. BARCODE / QR PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const BarcodePage = ({ companyId }) => {
  const [search, setSearch]       = useState('');
  const [products, setProducts]   = useState([]);
  const [selected, setSelected]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [labelSize, setLabelSize] = useState('medium');
  const [copies, setCopies]       = useState(1);
  const { show, ToastEl }         = useToast();

  const handleSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setProducts([]); return; }
    setLoading(true);
    const res = await productController.search(q, companyId);
    if (res.success) setProducts(res.data);
    setLoading(false);
  };

  const toggleSelect = (p) => setSelected(prev => prev.find(x=>x.id===p.id) ? prev.filter(x=>x.id!==p.id) : [...prev, p]);
  const isSelected   = (p) => selected.some(x=>x.id===p.id);

  const handlePrint = () => {
    if (!selected.length) { show('Select at least one product', 'error'); return; }
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Barcodes</title><style>
      body{font-family:Arial;margin:0;padding:10px}
      .label{display:inline-block;border:1px solid #ccc;padding:8px;margin:4px;text-align:center;${labelSize==='small'?'width:80px;font-size:9px':labelSize==='large'?'width:180px;font-size:13px':'width:120px;font-size:11px'}}
      .sku{font-family:monospace;font-weight:bold}
      @media print{.no-print{display:none}}
    </style></head><body>
    ${selected.map(p=>Array.from({length:copies},()=>`<div class="label"><div>${p.name}</div><div class="sku">${p.barcode||p.sku||'N/A'}</div><div>${p.sellingPrice?'₹'+p.sellingPrice:''}</div></div>`).join('')).join('')}
    </body></html>`);
    w.document.close(); w.print();
  };

  return (
    <div style={S.page}>
      {ToastEl}
      <PageHeader title="Barcode / QR" subtitle="Generate and print product barcodes" Icon={QrCode} color={C.purple} />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:16 }}>
        {/* Left: product search */}
        <div style={S.card}>
          <div style={{ padding:16 }}>
            <div style={S.secTitle}>Search & Select Products</div>
            <div style={{ position:'relative', marginBottom:14 }}>
              <Search size={15} color={C.textMuted} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)' }}/>
              <input style={{ ...S.input, paddingLeft:34 }} placeholder="Search products by name or SKU..." value={search} onChange={e=>handleSearch(e.target.value)} />
            </div>
            {loading && <div style={{ textAlign:'center', color:C.textMuted, padding:20 }}>Searching...</div>}
            {products.map(p=>(
              <div key={p.id||p._id} onClick={()=>toggleSelect(p)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', borderRadius:8, cursor:'pointer', marginBottom:6, background: isSelected(p) ? `${C.primary}12` : '#f9fafb', border:`1px solid ${isSelected(p)?C.primary:C.border}`, transition:'all .15s' }}>
                <div style={{ width:20, height:20, borderRadius:4, border:`2px solid ${isSelected(p)?C.primary:C.border}`, background:isSelected(p)?C.primary:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {isSelected(p) && <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>✓</span>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{p.name}</div>
                  <div style={{ fontSize:11, color:C.textMuted, fontFamily:'monospace' }}>{p.barcode||p.sku} • {money(p.sellingPrice)}</div>
                </div>
              </div>
            ))}
            {selected.length > 0 && (
              <div style={{ marginTop:14, padding:'10px 12px', background:`${C.success}10`, borderRadius:8, border:`1px solid ${C.success}30` }}>
                <div style={{ fontSize:12, color:C.success, fontWeight:700 }}>✓ {selected.length} product(s) selected</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:6 }}>
                  {selected.map(p=><Badge key={p.id} color={C.primary} bg={`${C.primary}12`}>{p.name} <span style={{ cursor:'pointer' }} onClick={()=>toggleSelect(p)}>×</span></Badge>)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: print options */}
        <div>
          <div style={{ ...S.card, padding:16, marginBottom:12 }}>
            <div style={S.secTitle}>Label Settings</div>
            <FormGrid cols={1}>
              <FormField label="Label Size">
                <select style={S.select} value={labelSize} onChange={e=>setLabelSize(e.target.value)}>
                  <option value="small">Small (80×40mm)</option>
                  <option value="medium">Medium (100×60mm)</option>
                  <option value="large">Large (150×80mm)</option>
                </select>
              </FormField>
              <FormField label="Copies per Product">
                <input type="number" min="1" max="100" style={S.input} value={copies} onChange={e=>setCopies(Math.max(1,Number(e.target.value)))} />
              </FormField>
            </FormGrid>
          </div>

          <div style={{ ...S.card, padding:16 }}>
            <div style={S.secTitle}>Preview</div>
            {selected.length > 0 ? (
              selected.slice(0,3).map(p=>(
                <div key={p.id} style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'12px', textAlign:'center', marginBottom:8, background:'#fff' }}>
                  <div style={{ fontSize:11, color:C.textSoft, marginBottom:4 }}>{p.name}</div>
                  <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:13, background:'#f3f4f6', padding:'4px 8px', borderRadius:4, marginBottom:4 }}>{p.barcode||p.sku||'N/A'}</div>
                  {p.sellingPrice > 0 && <div style={{ fontSize:12, fontWeight:700, color:C.primary }}>{money(p.sellingPrice)}</div>}
                </div>
              ))
            ) : (
              <div style={{ textAlign:'center', color:C.textMuted, padding:'20px 0', fontSize:13 }}>Select products to preview</div>
            )}
            <button style={{ ...S.btnPri, width:'100%', justifyContent:'center', marginTop:8 }} onClick={handlePrint}>
              <Download size={15}/> Print Labels ({selected.length} × {copies})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default BarcodePage;
